import './SingleChartView.scss';
import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'react-bootstrap';
import { MdArrowBack, MdClose, MdEdit, MdLockOutline, MdOutlineFileDownload } from 'react-icons/md';
import {
  ChartConfig,
  ChartDateRange,
  ChartId,
  DimensionId,
  GetChartUsageResponse,
  KpiId,
  QueryConfig,
  QueryResponse,
  ResolvedRoleLevels,
  resolveComparison
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  breakdownOptionsFor,
  ChartContextInfo,
  ChartFilterLine,
  MetricPickerChart
} from '@Components/ChartRenderer';
import {
  chartExportFileName,
  chartExportSheets,
  chartHasData
} from '@Components/ChartRenderer/chart-actions-menu-utils';
import { useFieldMetaCtx } from '@Components/ChartRenderer/useFieldMetaCtx';
import { Button } from '@Components/Button/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { IconButton } from '@Components/IconButton/IconButton';
import { AlertDialog } from '@Components/AlertDialog';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useDebouncedCallback } from '../../utils/hooks/useDebouncedCallback';
import {
  getSavedKpiId,
  kpiOptionsFor,
  toChartDateRange,
  toPickerRange,
  usesKpiPicker
} from '../Dashboard/date-range';
import { buildDashboardFilterChips } from '../Dashboard/dashboard-filter-utils';
import {
  FocusedViewFilters,
  fromFocusedViewFilterSearch
} from '../Dashboard/dashboard-view-params';
import {
  SaveAsNewPlacement,
  SaveAsNewPlacementType,
  SaveChartAsNewDialog
} from '../Dashboard/SaveChartAsNewDialog';
import { buildChartDataQueryKey, chartEnvelopeKey, useChartData } from './hooks/useChartData';
import { useWorkingCopy } from './hooks/useWorkingCopy';
import { chartFansOut, useChartUsage } from './hooks/useChartUsage';
import { useChartEditGate } from './hooks/useChartEditGate';
import { useSharingRequest } from './hooks/useSharingRequest';
import { usePendingSharing } from './hooks/usePendingSharing';
import { ChartEditExits } from './ChartEditExits';
import { NoEditAccessDialog } from './NoEditAccessDialog';
import { PermissionDropdown } from './PermissionDropdown';
import { ViewOnlyBadge } from './ViewOnlyBadge';
import { PromotionIntent, usePendingPromotion } from './hooks/usePendingPromotion';
import { FetchError } from '../../utils/fetch-error';
import { ChartHandle, isDraftId } from './chart-handle';
import { chartLibraryPath, dashboardReturnPath, readChartRouteState } from './chart-routes';
import { ChartViewParams, fromChartViewSearch, withChartViewParams } from './chart-view-params';
import { configsDiffer } from './chart-config-diff';
import { copyRoleLevels, saveChart } from './chart-save';
import { roleLevelLabel } from './chart-permissions';
import { resolveRequestedLevels, SharingTargetState } from './sharing-request-utils';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';

const CHART_ENTITY = 'chart';
// Longer than the 250ms most search/filter debounces in the app use: committing a title schedules a
// real /getData fetch (via useChartData's config-keyed query), not a local recompute, so it's worth
// giving a fast typist more room before that cost fires per keystroke.
const TITLE_COMMIT_DEBOUNCE_MS = 400;

interface SingleChartViewProps {
  company: string;
  // A saved `chart:<uuid>` or a brand-new, unsaved `draft:<uuid>`.
  handle: ChartHandle;
  // Set when the chart is shown as a modal over the chart library (see ChartsPage): renders the view
  // in that modal, with a close button that calls this, instead of as a full page.
  onClose?: () => void;
}

// Name the dashboards a chart is on, for the fan-out confirm: "Growth Weekly", "Growth Weekly and 1
// other", "Growth Weekly and 2 others". Falls back to a generic phrase if none are named.
const describeUsage = (usage: GetChartUsageResponse): string => {
  const [first, ...rest] = usage.dashboards;
  if (!first) return 'other dashboards';
  if (rest.length === 0) return first.name;
  return `${first.name} and ${conditionallyPluralizeWithCount(rest.length, 'other')}`;
};

// A saved chart on its own — no dashboard required. Loads config + data and renders through the
// same MetricPickerChart used in dashboard cards, so a chart looks identical wherever it appears.
// Wrapped in the same ChartContainer every other chart surface uses, sized to fill the whole page
// (see SingleChartView.scss) rather than a dashboard grid cell.
//
// The chart's title lives inside ChartContainer's own header (matching every other chart surface) —
// the page's own <Page title> stays blank rather than repeating it in the purple bar. The title itself
// edits exactly like DashboardCardTitle's does: the title slot swaps for a bare Form.Control that
// commits into a new working copy on every keystroke (the SAME mechanism the agent uses for a proposed
// chart edit — title is a chart-level property that can be shared across dashboards, so it goes through
// the existing Update/Save-as-new/Discard exits below rather than saving straight through, same as any
// other pending chart edit). There's no title-specific Save/Discard pair — same as DashboardCardTitle —
// those exits are the only way a title edit resolves.
// The "Edit Chart" toggle itself stays above the container, on the right — there's no page-wide edit
// mode here to gate the title the way DashboardHeader's isEditMode gates DashboardCardTitle, so this
// page needs its own toggle. Toggling it off doesn't touch a pending working copy, same as leaving
// Dashboard's edit mode doesn't resolve a card's pending edit either. The "Back to dashboard" button is
// a separate, non-editing nav concern and shares that same row.
//
// When the agent proposes an edit it lands in the WORKING COPY (not the saved row): the chart then
// renders that unsaved config as a preview, an "Unsaved changes" chip shows, and the user promotes it
// via explicit exits — for a saved chart: Update everywhere it's used (in-place), Save as a new chart
// (original untouched), or Discard; for a brand-new draft (no saved row yet): Save chart or Discard
// this draft. Transient view-state picks (date/KPI/metric/breakdown) are a way of looking, not an edit:
// they render on top but never trip the chip, never surface the exits, and are never persisted to the
// chart itself — they DO mirror into the URL (see chart-view-params) so a reload or a shared link
// carries the same view, which is a navigation concern, not a chart-definition one.
export const SingleChartView = ({ company, handle, onClose }: SingleChartViewProps) => {
  const { cross_channel_kpis: crossChannelKpis } = useCompanyInfo();
  const navigate = useNavigate();
  // Set only when this chart was opened from a dashboard card's "Open in focused view" (see
  // DashboardCard's navigate call) — carried as navigation state rather than a URL param, since it's
  // context for how we got here, not a property of the chart a direct/shared link should imply.
  const { state: locationState } = useLocation();
  const { fromDashboardId, fromDashboardSearch } = readChartRouteState(locationState);
  const queryClient = useQueryClient();
  const setError = useSetError();
  // A brand-new chart the agent built from nothing, not yet saved. It has no saved row: it renders
  // purely from its working copy and promotes via Save chart / Discard this draft.
  const isDraft = isDraftId(handle);
  // The view this chart was opened THROUGH, if any: a dashboard card writes what it was showing into
  // the URL when it expands, and this view mirrors its own picker changes back (see chart-view-params).
  // Read once at mount — from here on this component's state is the source of truth and the URL
  // follows it, so a picker change doesn't have to round-trip through the router to take effect.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedWith] = useState<ChartViewParams>(() => fromChartViewSearch(searchParams));
  // The dashboard filters the chart was expanded with. There is no picker here, so they stay as opened.
  const [openedWithFilters] = useState<FocusedViewFilters>(() =>
    fromFocusedViewFilterSearch(searchParams)
  );
  // Transient overrides: undefined keeps the chart's own saved values until the user picks one (or
  // the URL supplies one).
  const [override, setOverride] = useState<ChartDateRange | undefined>(openedWith.dateRange);
  const [comparisonOverride, setComparisonOverride] = useState<ChartDateRange | undefined>(
    openedWith.comparisonRange
  );
  const [kpiOverride, setKpiOverride] = useState<KpiId | undefined>(openedWith.kpiId);
  const [metricOverride, setMetricOverride] = useState<string | null>(openedWith.metric ?? null);
  // The breakdown pick: what the chart is grouped and pivoted by. Each pick is its own fetch
  // (applyBreakdown narrows the request to it).
  const [breakdownPick, setBreakdownPick] = useState<DimensionId | null>(
    openedWith.breakdown ?? null
  );
  const [saving, setSaving] = useState(false);
  // The fan-out confirm interstitial: shown before an in-place Update when the chart is on a
  // dashboard the user doesn't solely own (§6). A solely-owned chart updates with no interstitial.
  const [showFanOutConfirm, setShowFanOutConfirm] = useState(false);
  // The naming + placement dialog for "Save as a new chart" — same SaveChartAsNewDialog the dashboard
  // card's edit controls use.
  const [showSaveAsNewDialog, setShowSaveAsNewDialog] = useState(false);
  // The rename toggle and its live draft (mirrors DashboardCardTitle's own draftTitle — see the
  // handlers below). Declared unconditionally, ahead of the early returns below, like every other
  // hook here.
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  // Snapshot of the working copy as it stood the moment title-editing began — so Cancel can restore
  // exactly that instead of wiping the whole thing. A pending edit can already exist before the user
  // ever opens the title editor (e.g. an agent-proposed change): Cancel must undo only this session's
  // title typing, not discard that unrelated edit too.
  const [workingCopyOnEntry, setWorkingCopyOnEntry] = useState<ChartConfig | null>(null);

  // The saved row (for the "did my edit stick?" diff) and the working copy (the agent's unsaved edit).
  // When a working copy is present it drives the render as `baseConfig`; otherwise a saved chart
  // renders its saved config, fetched inside useChartData. A draft has NO saved row, so don't try to
  // /getChart it — it renders solely from the working copy.
  // The whole saved record, not just its config: the gate needs both role levels, and the refusal
  // dialog needs `createdBy` to name the owner. Same cache entry `useChartConfig` reads, so this
  // costs no extra request.
  //
  // This is the SAME hook a dashboard card's edit controls use, so the two surfaces cannot gate an
  // Update differently. A draft passes null — no saved row, nothing governed, every flag false and
  // the gate inert, which is right: a draft is the user's own until they first save it.
  const {
    envelope,
    error: envelopeError,
    userRole,
    isResolved,
    isChartRestricted,
    isViewOnly,
    isWriteBlocked,
    showNoEditAccess,
    setShowNoEditAccess,
    handleForbidden
  } = useChartEditGate(company, isDraftId(handle) ? null : handle);
  const savedConfig = envelope?.config;
  const { workingCopy, setWorkingCopy, clearWorkingCopy } = useWorkingCopy(company, handle);

  const overrides = {
    dateRange: override,
    comparisonRange: comparisonOverride,
    kpiId: kpiOverride,
    breakdown: breakdownPick ?? undefined,
    ...openedWithFilters
  };

  // Mirror the current view back into the URL, so it survives a reload and a copied link hands
  // someone else the same chart in the same terms. An EFFECT over the state rather than a write
  // inside each picker's handler: one Apply on the date picker fires the primary and comparison
  // callbacks back-to-back, so a handler merging its own change into the previous render's state
  // would write the second over a stale copy of the first. Replaces rather than pushes — flipping a
  // metric isn't a step the back button should have to walk back through — and no-ops when the URL
  // already says this, so it can't loop with the searchParams identity it depends on. Passes the
  // location state through: a URL write without it would drop how this chart was opened — the chart
  // library modal (fromChartLibrary) or the "Back to dashboard" button (fromDashboardId).
  useEffect(() => {
    const next = withChartViewParams(searchParams, {
      dateRange: override,
      comparisonRange: comparisonOverride,
      kpiId: kpiOverride,
      metric: metricOverride ?? undefined,
      breakdown: breakdownPick ?? undefined
    });
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true, state: locationState });
  }, [
    searchParams,
    setSearchParams,
    locationState,
    override,
    comparisonOverride,
    kpiOverride,
    metricOverride,
    breakdownPick
  ]);
  const { data, isLoading, isPlaceholderData, isError, error } = useChartData(
    company,
    handle,
    overrides,
    workingCopy ?? undefined
  );

  // Debounces the title's working-copy commit (see handleTitleChange below) so a fast typist doesn't
  // fire a fresh /getData request per character — only scheduling, not the keystroke handling itself,
  // needs to live up here ahead of the early returns (Rules of Hooks); `data`/`savedConfig` are read
  // from the closure at the time the timer actually fires, not when it was scheduled.
  const [scheduleTitleCommit, cancelTitleCommit] = useDebouncedCallback((trimmed: string) => {
    if (!data) return;
    setWorkingCopy({ ...data.config, title: trimmed || (savedConfig?.title ?? data.config.title) });
  }, TITLE_COMMIT_DEBOUNCE_MS);

  // A breakdown carried in on a link outlives the session that made it: the chart's controls can be
  // edited (or the breakdown control dropped) long after someone bookmarked or shared it. Unlike the
  // other view params this one goes into the QUERY, where applyBreakdown already ignores a pick that
  // isn't on offer and fetches the default instead. Drop it as soon as the config says it isn't on
  // offer so the picker agrees; the URL effect above then drops the param too, so the link heals
  // itself. A pick made HERE is always an option,
  // so this only ever fires for one that arrived from outside.
  const configForPicks = workingCopy ?? savedConfig;
  useEffect(() => {
    if (!configForPicks || !breakdownPick) return;
    if (breakdownOptionsFor(configForPicks).includes(breakdownPick)) return;
    setBreakdownPick(null);
  }, [configForPicks, breakdownPick]);

  // A brand-new draft with no working copy is unrecoverable (it never had a saved row) — warn before
  // the tab is closed or reloaded away, since sessionStorage doesn't survive a tab close. A saved
  // chart's unsaved edits are droppable (the saved row remains), so no guard there.
  const guardUnload = isDraft && Boolean(workingCopy);
  useEffect(() => {
    if (!guardUnload) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guardUnload]);

  // "Unsaved changes": the chart DEFINITION differs from what's saved (agent edits only) — always true
  // for a draft (there's nothing saved yet). Picker overrides are transient view-state, not edits, so
  // they never count here and get no indicator.
  const hasUnsavedChanges = Boolean(
    workingCopy && (!savedConfig || configsDiffer(workingCopy, savedConfig))
  );

  // The chart's blast radius, fetched only when there's an unsaved edit on a SAVED chart (never for a
  // draft — it's on no dashboard yet). Drives the persistent "used in N places" scope cue and the
  // fan-out confirm below.
  const { data: usage, isLoading: usageLoading } = useChartUsage(
    company,
    isDraftId(handle) ? null : handle,
    hasUnsavedChanges && !isDraft
  );

  // A chat-driven promotion ("save it" / "make a new one" / "discard") the agent routed here; consumed
  // by the effect below, which runs the SAME exit handlers the buttons do.
  //
  // Read AHEAD of `useSharingRequest` below (it has no dependency on that hook) so its value is in
  // scope for `defer`: a promotion and a sharing change can both be queued in the same commit, and
  // both would `saveChart` this same chart id — see the note on `defer` for why the sharing write
  // must wait for the promotion instead of racing it.
  const { pendingPromotion, clearPendingPromotion } = usePendingPromotion(company, handle);

  // Change who may see and who may change this chart.
  //
  // ⚠️ SENDS THE SAVED CONFIG, NEVER THE WORKING COPY. `saveChart` writes whatever config it is
  // given, so passing `workingCopy` here would publish an unsaved edit to every reader as a side
  // effect of changing the audience — and nothing on screen would show that it had happened. The
  // envelope's own `config` is the row as the server currently holds it, which is what a pure
  // audience change must leave behind.
  //
  // Rethrows after reporting, which is this handler's half of `PermissionDropdown`'s contract: the
  // dialog stays open on the user's selection and tracks nothing when the save did not land. A 403
  // is a live possibility here — the gate this feature added refuses the write server-side.
  const handlePermissionChange = async (roles: ResolvedRoleLevels) => {
    if (!envelope) return;
    // `saving` is the page's ONE in-flight-write flag, and this write has to raise it like every
    // other. It makes the mutual exclusion with Update symmetric: `defer` below already holds a chat
    // sharing request while an Update is in flight, and this is the other half — the Update button
    // takes `loading={saving}`, so it cannot be clicked while this PUT is out. Without it both
    // paths `saveChart` the same id, this one with the PRE-EDIT `envelope.config`, and if it lands
    // second it silently reverts the user's just-saved edit. The dialog used to make that
    // impossible by having a modal backdrop; a chat-driven write opens no dialog at all.
    setSaving(true);
    try {
      const saved = await saveChart(company, envelope.config, roles);
      // Server truth, not the pair we asked for: the backend clamps both levels.
      queryClient.setQueryData(chartEnvelopeKey(company, handle), saved);
    } catch (e) {
      setError({
        message: `Couldn't change who can see this chart: ${e.message}`,
        reportError: e
      });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // A chat-driven sharing change. Routed through the SAME handler the dialog's Save calls, so the
  // working-copy hazard and the 403 path are covered once. `openWith` opens that dialog pre-filled
  // when the change would let more people see the chart.
  //
  // `noteTarget` is NULL for a draft, exactly as `useChartEditGate` above takes a null chart id for
  // one. A draft has no row to write, and its request is read by `performSaveAsNew` and the footer
  // note instead — so this hook must neither act on it nor clear it.
  const { openWith: sharingConfirm, closeConfirm } = useSharingRequest({
    company,
    noteTarget: isDraft ? null : handle,
    // envelope present wins, then a refused read, then still-loading — not a ternary chain
    // (no-nested-ternary) since there are three arms to pick among, not two.
    target: (() => {
      if (envelope) return { state: SharingTargetState.GOVERNED, levels: envelope };
      // ANY terminal failure, not just a 403. The envelope query does not retry a 4xx on mount, so a
      // 404 on a deleted chart leaves us with no envelope and no error status we recognise — and
      // LOADING means "wait", which here would mean waiting forever while the user's request is
      // never acted on and never cleared.
      if (isChartRestricted || envelopeError) return { state: SharingTargetState.UNREADABLE };
      return { state: SharingTargetState.LOADING };
    })(),
    userRole,
    canEdit: !isWriteBlocked,
    page: PAGE_NAMES.CHARTS,
    // Another writer owns this row: a chat promotion is queued, its save is in flight, OR the
    // fan-out confirmation is waiting on a click. That third state is easy to miss — the promotion
    // effect clears its note BEFORE calling handleUpdate, and the fan-out branch starts no save, so
    // without it `defer` goes false while an Update is still pending on a human and both paths
    // `saveChart` the same chart id. The loser silently reverts the other.
    defer: Boolean(pendingPromotion) || saving || showFanOutConfirm,
    onSave: handlePermissionChange
  });

  // A draft's requested audience, held from the turn that asked for it until the chart is first
  // saved. `useSharingRequest` above is passed a NULL note target for a draft, so it neither reads
  // nor clears this note — which is what leaves it here for the draft's own save path to apply.
  const { pendingSharing, clearPendingSharing } = usePendingSharing(company, handle);
  const draftSharing = isDraft ? pendingSharing : null;

  // A draft skips `decideSharingRequest` — there is no stored row to compare a request against — so
  // the two rules that DO apply (every named audience must be assignable; the ceiling over the
  // finished pair) run through `resolveRequestedLevels`, the same function the dashboard's
  // new-chart path calls. `null` levels mean the request named an audience this user cannot assign.
  const effectiveDraftLevels = draftSharing
    ? resolveRequestedLevels(draftSharing, userRole).levels
    : null;
  // A BOOLEAN, so the effect below has a stable dep: `effectiveDraftLevels` is a fresh object on
  // every render, and depending on it directly would re-run the effect on every render.
  const draftSharingUnassignable = draftSharing !== null && effectiveDraftLevels === null;

  // Only whichever half the request actually named, post-ceiling — so the note never promises
  // anything about a field nobody asked about. This is also why the ceiling has to be applied before
  // the footer renders: what it shows here is what `performSaveAsNew` below sends.
  const draftFooterLevels = effectiveDraftLevels && {
    visibleTo:
      draftSharing?.visibleTo !== undefined ? effectiveDraftLevels.viewRoleRequired : undefined,
    editableBy:
      draftSharing?.editableBy !== undefined ? effectiveDraftLevels.editRoleRequired : undefined
  };

  // Told once, rather than silently handed a different audience than the one the footer promised.
  useEffect(() => {
    if (draftSharingUnassignable) {
      setError({
        message:
          "The audience you asked for isn't one you can assign, so it wasn't applied. You can " +
          "change who it's shared with after you save."
      });
    }
    // `draftSharing` stays in the list so a SECOND unassignable request is reported too — the note
    // is a cache value with a stable identity, so it changes only when a new request arrives.
  }, [draftSharing, draftSharingUnassignable, setError]);

  // Update everywhere it's used (saved charts only): persist the working copy in place (same id), then
  // seed the caches so clearing the working copy doesn't flash a redundant /getChart + /getData reload
  // — the saved row is now exactly the working copy we already compiled + rendered.
  const performUpdate = async () => {
    if (!workingCopy || !data) return;
    // Defensive. `handleUpdate` gates this, but the fan-out confirmation's main action calls this
    // function DIRECTLY, so a locked chart must not be able to reach a write through that path.
    if (isWriteBlocked) return;
    setSaving(true);
    try {
      const saved = await saveChart(company, workingCopy);
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.CHARTS, entity: CHART_ENTITY });
      // Seed the envelope cache with SERVER TRUTH, not the working copy. The entry holds a whole
      // DashboardChart now, and the server owns fields the client cannot compute — modifiedAt,
      // lastModifiedBy, and both clamped role levels.
      queryClient.setQueryData(chartEnvelopeKey(company, handle), saved);
      queryClient.setQueryData(buildChartDataQueryKey(company, handle, overrides, undefined), data);
      cancelTitleCommit();
      clearWorkingCopy();
      setIsEditingTitle(false);
    } catch (e) {
      // The server refused it, and the server is the authority — route to the same dialog the gate
      // would have shown (see useChartEditGate). This function has no `finally` that closes the
      // fan-out confirm, so close it here.
      if (e instanceof FetchError && e.status === 403) {
        setShowFanOutConfirm(false);
        await handleForbidden();
        return;
      }
      setError({ message: `Couldn't update this chart: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  // The Update gate: if the chart fans out (it's on a dashboard the user doesn't solely own), interpose
  // the confirm; otherwise update straight away. Same gate for the button and a chat-driven Update.
  const handleUpdate = () => {
    if (!workingCopy || !data) return;
    // THE EDIT CHECK RUNS FIRST, before the fan-out check below. Reverse the two and a locked chart
    // that other dashboards also use goes through the fan-out confirmation instead — and that
    // dialog's main action calls `performUpdate` directly, skipping this gate and surfacing a raw
    // 403 from the server.
    if (isWriteBlocked) {
      setShowNoEditAccess(true);
      return;
    }
    if (chartFansOut(usage)) {
      setShowFanOutConfirm(true);
      return;
    }
    performUpdate();
  };

  // Save as a new chart / Save chart (draft): strip the id so the backend mints a fresh chart:<uuid>
  // (leaving any original untouched) under the given name, clear this handle's working copy — for a
  // draft this is the promotion migration: the draft's cache + sessionStorage sidecar are dropped so a
  // future draft can't collide with or resurrect it. Mirrors DashboardCardEditControls' own
  // handleSaveAsNew (same SaveChartAsNewDialog, same placement choice) — HERE means this page follows
  // the new chart, same as "Show it here" repoints a dashboard card; LIBRARY leaves this page put.
  // Only clears the dialog on success (mirroring the dashboard's own controls) so a failed save leaves
  // it open to retry.
  const performSaveAsNew = async (name: string, placement: SaveAsNewPlacementType) => {
    if (!workingCopy) return;
    // A saved chart's copy inherits its audience, so the envelope has to be in hand. Refuse rather
    // than widen (see the note beside `copyRoleLevels` below). A draft has no envelope by design and
    // is unaffected.
    if (!isDraft && !envelope) {
      setError({ message: "Couldn't read this chart's sharing settings. Reload and try again." });
      return;
    }
    setSaving(true);
    try {
      const draft: Omit<ChartConfig, 'id'> & { id?: ChartId } = { ...workingCopy, title: name };
      delete draft.id;
      // A DRAFT's first save is the only place a chat-requested audience can land: there is no row
      // until now. A real copy takes `copyRoleLevels` instead, and a pending note must NOT override
      // it — a copy inherits its original's audience, and a stale note would publish it on terms the
      // original never had. `draftSharing` is null unless `isDraft`, so the `copyRoleLevels` branch
      // is unchanged for every saved chart, including the "envelope absent" refusal above, which a
      // SAVED chart must still hit rather than silently widen to the server's create default.
      //
      // `effectiveDraftLevels` is the RESOLVED pair — both levels, ceiling applied (see
      // `resolveRequestedLevels`) — and it is `null` when the request named a level this user cannot
      // assign, which falls through to the create defaults rather than silently landing at whatever
      // the backend would have clamped it to. Sending the resolved pair rather than the raw request
      // is what makes this write agree with what the footer just promised.
      const roleLevels = effectiveDraftLevels ?? copyRoleLevels(envelope, userRole);
      const { id: newId } = await saveChart(company, draft, roleLevels);
      Mixpanel.track(isDraft ? MxE.RecordCreated : MxE.RecordSaved, {
        page: PAGE_NAMES.CHARTS,
        entity: CHART_ENTITY
      });
      if (placement === SaveAsNewPlacement.HERE) {
        // Navigate first, then clear: batched into one render onto the new chart's route, so a draft
        // (which has no saved row to fall back to) never flashes its "no longer available" state.
        // Carries the location state forward so the new chart keeps however this one was opened — the
        // "Back to dashboard" button (fromDashboardId), or the chart-library modal (fromChartLibrary,
        // see ChartsPage). A bare navigate() doesn't inherit location state, so omitting this would
        // silently drop the one, or kick the other out of the modal into a full page, on save. In the
        // modal it also REPLACES this entry, so the entry behind the new chart is still the library —
        // which is what the modal's close button goes back to.
        navigate(`/${company}/charts/${newId}`, {
          state: locationState,
          replace: Boolean(onClose)
        });
      }
      cancelTitleCommit();
      clearWorkingCopy();
      // A draft's note was just applied above, so it is spent either way.
      //
      // A SAVED chart's note is normally `useSharingRequest`'s to consume, and this function must not
      // touch it — it is deliberately HELD while `saving` is true. But when this save NAVIGATES AWAY
      // there is nothing left mounted to consume it, and the note's `gcTime: Infinity` keeps it alive
      // to decide itself the next time somebody opens this chart — a sharing change applying
      // unattended, possibly weeks later, which is the hazard the whole note mechanism is written to
      // avoid. So drop it here, and SAY SO: a request that vanishes in silence is the other failure
      // this feature exists to remove.
      const leavingWithHeldNote =
        placement === SaveAsNewPlacement.HERE && !draftSharing && pendingSharing !== null;
      if (draftSharing || leavingWithHeldNote) {
        clearPendingSharing();
      }
      if (leavingWithHeldNote) {
        setError({
          message:
            "You're on the new chart now, so the audience change you asked for wasn't applied to " +
            'the original. Ask again on whichever chart you meant.'
        });
      }
      setShowSaveAsNewDialog(false);
      setIsEditingTitle(false);
    } catch (e) {
      setError({ message: `Couldn't save a new chart: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  // Instant save-as-new for callers that skip the naming dialog entirely: a draft's own "Save chart"
  // (there's no name to pick — it's the chart's first save) and a chat-driven "make a new one"
  // promotion — both keep today's title and land on the new chart, as before this dialog existed.
  const handleSaveAsNew = () => {
    if (!workingCopy) return;
    performSaveAsNew(workingCopy.title, SaveAsNewPlacement.HERE);
  };

  // Discard (saved chart): drop the working copy — the chart re-renders its saved config, staying put.
  // Also leaves title-edit mode: without this, discarding mid title-edit left the Form.Control showing
  // stale drafted text with no pending change behind it, and typing into it again would resurrect that
  // discarded draft as a fresh working copy.
  const handleDiscard = () => {
    cancelTitleCommit();
    clearWorkingCopy();
    setIsEditingTitle(false);
  };

  // Discard this draft: a draft has no saved row to fall back to, so discarding destroys it entirely
  // and returns to the chart library.
  const handleDiscardDraft = () => {
    clearWorkingCopy();
    navigate(chartLibraryPath(company));
  };

  // Consume a chat-driven promotion by running the matching exit — the same handlers the buttons use,
  // so an agent "save it" hits the fan-out confirm exactly as clicking Update does. Wait for `data`
  // (the handlers need the rendered preview) and, for an in-place Update, for usage to settle (else
  // we'd skip the fan-out confirm). For a draft, update/save-as-new both save it as a new chart.
  useEffect(() => {
    if (!pendingPromotion || !data) return;
    // Wait for the ENVELOPE query, not just the render — they are two separate queries, and `data`
    // can be warm while the envelope is still in flight (seeded by a previous save, or refetching
    // after an invalidation).
    //
    // The gate is inert while the envelope is unresolved (see useChartEditGate), so a chat-driven
    // "save it" arriving in that window would skip the permission check entirely and attempt the
    // write — and a user who may not edit the chart would meet a raw 403 in the error modal, which
    // is the experience this whole pull request exists to remove.
    if (pendingPromotion === PromotionIntent.UPDATE && !isDraft && (usageLoading || !isResolved)) {
      return;
    }
    clearPendingPromotion();
    if (pendingPromotion === PromotionIntent.DISCARD) {
      if (isDraft) {
        handleDiscardDraft();
      } else {
        handleDiscard();
      }
    } else if (isDraft || pendingPromotion === PromotionIntent.SAVE_AS_NEW) {
      handleSaveAsNew();
    } else {
      handleUpdate();
    }
    // Handlers are intentionally omitted: the effect fires on a fresh promotion/data/usage change and
    // reads the current closures; adding the (unmemoized) handlers would only churn the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPromotion, data, usageLoading, isDraft, isResolved]);

  if (isLoading && !data) {
    return (
      <ChartViewFrame onClose={onClose}>
        <div className='single-chart-view'>
          <Spinner />
        </div>
      </ChartViewFrame>
    );
  }
  // A draft whose working copy is gone (discarded elsewhere, or a stale/shared URL in a tab that never
  // held it) can't be rendered — there's no saved row behind it. Say so instead of failing to /getChart.
  if (isDraft && !workingCopy) {
    return (
      <ChartViewFrame onClose={onClose}>
        <div className='single-chart-view single-chart-view-message'>
          This draft is no longer available.
        </div>
      </ChartViewFrame>
    );
  }
  // The server declined to send this chart — most often met through a pasted link, or after
  // somebody narrowed the audience while this tab was open.
  //
  // Checked BEFORE the `!data` branch below, because `data` can EXIST for a chart the server
  // refuses to send: `useChartData` skips /getChart whenever it has a working copy and renders that
  // copy through /getData, which is ungated by design. So a working copy left in sessionStorage
  // would otherwise keep the whole editor open on a chart this user may no longer read.
  //
  // The only locked branch on THIS page — a dashboard card draws its own, see RestrictedCard in
  // Dashboard/cards/ChartCard.tsx. On a cold load both queries call /getChart with the same company
  // and id, so a 403 reaches here first. They are separate cache entries though, each with its own
  // staleTime, so the two can disagree: a warm envelope that resolved before somebody narrowed the
  // audience keeps returning success while the render query 403s on a fresh key. The `!data` branch
  // below catches that window, and the envelope's next fetch settles it.
  if (isChartRestricted) {
    return (
      <ChartViewFrame onClose={onClose}>
        <div className='single-chart-view single-chart-view-message single-chart-view-locked'>
          <MdLockOutline size={24} />
          <span>You don&apos;t have access to this chart.</span>
          <span>Ask its owner for access.</span>
        </div>
      </ChartViewFrame>
    );
  }

  // No data at all (initial load failed). A later failure while previewing keeps the prior chart on
  // screen via keepPreviousData, so this only fires when the chart never loaded. A 404 means the
  // chart is GONE rather than unreachable — a deleted chart, or a stale link to one — which is worth
  // saying plainly, since retrying or reloading will never help. Same distinction the dashboard card
  // draws.
  if (!data) {
    const chartIsGone = error instanceof FetchError && error.status === 404;
    return (
      <ChartViewFrame onClose={onClose}>
        <div className='single-chart-view single-chart-view-message'>
          {chartIsGone ? 'This chart no longer exists.' : "Couldn't load this chart."}
        </div>
      </ChartViewFrame>
    );
  }

  // Declaration-first KPI picker visibility: a declared `kpi` control shows the picker; absent one,
  // fall back to inferring it from any KPI-using query. Default to the override, else the saved KPI.
  const usesKpi = usesKpiPicker(data.config);
  const currentKpi = kpiOverride ?? getSavedKpiId(data.config);
  const kpiPicker =
    usesKpi && currentKpi
      ? {
          options: kpiOptionsFor(crossChannelKpis, currentKpi),
          value: currentKpi,
          onChange: setKpiOverride
        }
      : undefined;

  const stripToQueryConfig = (): QueryConfig => ({
    queries: data.config.queries,
    merge: data.config.merge,
    comparison: data.config.comparison
  });

  const handleLogConfig = () => {
    const queryConfig = stripToQueryConfig();

    // Log the object (easier to view in DevTools)
    console.info('[SingleChartView] Query config:\n', queryConfig);
  };

  // Rename: mirrors DashboardCardTitle's handleChange — debounced-commits into a new working copy
  // rather than buffering to a separate Save/Discard pair (a title is a chart-level property that can
  // be shared across dashboards, so it goes through the existing Update/Save-as-new/Discard exits below
  // rather than saving straight through). A cleared or reverted-to-saved field resolves back to the
  // chart's actual saved title instead of being skipped — `data.config.title` is the CURRENT effective
  // title (already reflecting an earlier keystroke's commit), so skipping there would leave the working
  // copy pinned to whatever was last typed once the user backs all the way out of an edit.
  const handleTitleChange = (value: string) => {
    setTitleDraft(value);
    scheduleTitleCommit(value.trim());
  };

  // The toggle itself (button lives above the container — see the render below). Entering seeds the
  // draft from the current title (same as DashboardCardTitle re-seeding on a discard) and snapshots
  // whatever working copy already existed. "Cancel" while a title edit is still pending discards ONLY
  // this session's title typing — restoring that snapshot exactly rather than clearing outright, so an
  // unrelated pre-existing pending edit (e.g. an agent-proposed change) survives a title-edit Cancel.
  // Committing a real change is still the explicit Update/Save-as-new exits' job either way.
  const handleToggleEditTitle = () => {
    if (isEditingTitle) {
      cancelTitleCommit();
      if (workingCopyOnEntry) {
        setWorkingCopy(workingCopyOnEntry);
      } else {
        clearWorkingCopy();
      }
    } else {
      setWorkingCopyOnEntry(workingCopy);
      setTitleDraft(data.config.title);
    }
    setIsEditingTitle(!isEditingTitle);
  };

  const title = isEditingTitle ? (
    <Form.Control
      className='single-chart-view-title-input'
      value={titleDraft}
      placeholder='Chart title'
      onChange={e => handleTitleChange(e.target.value)}
    />
  ) : (
    <div className='single-chart-view-title'>
      <span>{data.config.title}</span>
      {/* A saved chart's edit gets its "Unsaved changes" label on the ChartEditExits group instead. */}
      {hasUnsavedChanges && isDraft && (
        <span className='single-chart-view-chip'>Unsaved changes</span>
      )}
      <ChartContextInfo config={data.config} />
    </div>
  );

  // The focused view ALWAYS shows a date picker — a chart must always be re-scopable by date, even
  // when it declares other controls. The `dateRange` control governs whether a chart FOLLOWS the
  // dashboard's global picker vs. keeps an independent range (a link hint, handled by the dashboard
  // card) — it is NOT a presence switch. There is no global picker to link to here, so it always
  // renders its own. Sized Size.MD to match every other ChartContainer's header controls.
  const rightActions = (
    <>
      {/* Who this chart is shared with, or a note that the user may only read it. This slot sits
          BELOW the early returns above, so it only ever covers a readable chart — one they cannot
          see never reaches here. `envelope` is the "there is a row to govern" test: absent for a
          draft and while /getChart is in flight, and in both cases neither arm should draw. */}
      {envelope &&
        (isViewOnly ? (
          <ViewOnlyBadge />
        ) : (
          <PermissionDropdown
            viewRoleRequired={envelope.viewRoleRequired}
            editRoleRequired={envelope.editRoleRequired}
            page={PAGE_NAMES.CHARTS}
            openWith={sharingConfirm}
            onExternalClose={closeConfirm}
            onSave={handlePermissionChange}
          />
        ))}
      {/* The three exits for a SAVED chart's pending edit — the same ChartEditExits group as
          DashboardCardEditControls (same labels, dialogs), just triggered from this page's own
          handlers rather than a dashboard card's. A draft has its own Save chart/Discard this draft
          pair below instead (a dashboard card never hits that state, so there's nothing there to
          mirror). */}
      {hasUnsavedChanges && !isDraft && (
        <ChartEditExits
          saving={saving}
          onUpdate={handleUpdate}
          onSaveAsNew={() => setShowSaveAsNewDialog(true)}
          onDiscard={handleDiscard}
        />
      )}
      <CopyButton
        text={JSON.stringify(stripToQueryConfig(), null, 2)}
        tooltipLabel='Copy QueryConfig to clipboard & log'
        onCopied={handleLogConfig}
        aria-label='Log query config and copy to clipboard'
      />
      {resolveComparison(data.config) ? (
        <DatePicker
          range={toPickerRange(override ?? data.config.queries[0]?.dateRange)}
          onChange={(r: DateRange) => setOverride(toChartDateRange(r))}
          comparison
          comparisonRange={toPickerRange(comparisonOverride ?? data.config.queries[1]?.dateRange)}
          comparisonOnChange={(r: DateRange) => setComparisonOverride(toChartDateRange(r))}
          datePresets
          design={Design.NEUTRAL}
          size={Size.MD}
        />
      ) : (
        <DatePicker
          range={toPickerRange(override ?? data.config.queries[0]?.dateRange)}
          onChange={(r: DateRange) => setOverride(toChartDateRange(r))}
          datePresets
          design={Design.NEUTRAL}
          size={Size.MD}
        />
      )}
      <ChartDownloadButton config={data.config} data={data.data} />
    </>
  );

  const header = (
    <>
      {fromDashboardId && (
        <Button
          className='single-chart-view-back'
          size={Size.LG}
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          leadingElement={<MdArrowBack />}
          onClick={() =>
            navigate(dashboardReturnPath(company, fromDashboardId, fromDashboardSearch))
          }
        >
          Back to dashboard
        </Button>
      )}
      <Button
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        leadingElement={isEditingTitle ? undefined : <MdEdit />}
        onClick={handleToggleEditTitle}
      >
        {isEditingTitle ? 'Cancel' : 'Edit Chart'}
      </Button>
    </>
  );

  return (
    <ChartViewFrame onClose={onClose} header={header}>
      <ChartContainer
        className='single-chart-view single-chart-view-chart-container'
        title={title}
        // The chart's filters, as a small line directly under the title (same as a dashboard card).
        leftActions={
          <ChartFilterLine
            config={data.config}
            data={data.data}
            breakdown={breakdownPick}
            dashboardFilterChips={buildDashboardFilterChips(
              data,
              openedWithFilters.ignoreDashboardFilters
            )}
          />
        }
        rightActions={rightActions}
        viewRightActions
        subHeader={
          hasUnsavedChanges && (isError || isDraft) ? (
            <div className='single-chart-view-exits'>
              {isError && (
                <span className='single-chart-view-render-error'>
                  Couldn&apos;t render that change — try a different edit or discard.
                </span>
              )}
              {isDraft && (
                <>
                  {draftFooterLevels && (
                    <PendingSharingNote
                      visibleTo={draftFooterLevels.visibleTo}
                      editableBy={draftFooterLevels.editableBy}
                    />
                  )}
                  <Button size={Size.SM} loading={saving} onClick={handleSaveAsNew}>
                    Save chart
                  </Button>
                  <Button
                    size={Size.SM}
                    appearance={Appearance.EMPTY}
                    disabled={saving}
                    onClick={handleDiscardDraft}
                  >
                    Discard this draft
                  </Button>
                </>
              )}
            </div>
          ) : undefined
        }
      >
        <MetricPickerChart
          config={data.config}
          data={data.data}
          kpiPicker={kpiPicker}
          metric={metricOverride}
          onMetricChange={setMetricOverride}
          breakdown={breakdownPick}
          onBreakdownChange={setBreakdownPick}
          isRefetching={isPlaceholderData}
          showTitle={false}
        />
      </ChartContainer>
      {usage && (
        <AlertDialog
          intent={Intent.WARNING}
          show={showFanOutConfirm}
          onHide={() => setShowFanOutConfirm(false)}
          title="Update everywhere it's used?"
          body={
            <>
              This chart is on <strong>{describeUsage(usage)}</strong> — updating changes it for
              everyone who sees them.
            </>
          }
          primaryAction={{ onClick: performUpdate, label: 'Update anyway' }}
          secondaryAction={{ onClick: () => {} }}
          loading={saving}
          tertiaryAction={{
            onClick: () => setShowSaveAsNewDialog(true),
            label: 'Save as a new chart',
            design: Design.NEUTRAL
          }}
        />
      )}
      <NoEditAccessDialog
        show={showNoEditAccess}
        onHide={() => setShowNoEditAccess(false)}
        onSaveAsNew={() => setShowSaveAsNewDialog(true)}
        createdBy={envelope?.createdBy ?? null}
      />
      {workingCopy && (
        <SaveChartAsNewDialog
          show={showSaveAsNewDialog}
          defaultName={workingCopy.title ?? ''}
          loading={saving}
          onCancel={() => setShowSaveAsNewDialog(false)}
          onSave={performSaveAsNew}
        />
      )}
    </ChartViewFrame>
  );
};

interface ChartViewFrameProps extends PropsWithChildren {
  // See SingleChartView's own `onClose`: present means "render as the chart-library modal".
  onClose?: () => void;
  // The nav/edit controls row above the chart. Only the loaded state has any; the modal still draws
  // the row without them, since it's where the close button lives.
  header?: ReactNode;
}

// What every state of SingleChartView (loading, error, loaded) renders inside: a full <Page> for a
// chart reached directly, or — opened from the chart library — a modal over that library, whose
// close button returns to it (see ChartsPage). A fake modal rather than a react-bootstrap <Modal>:
// the chart has its own URL, and it's ChartsPage, not this component, that decides from the route
// whether it's showing. It also has to sit BELOW the real modals this view opens (the fan-out
// confirm, Save as a new chart), which a Bootstrap <Modal> of its own would stack on top of.
const ChartViewFrame = ({ onClose, header, children }: ChartViewFrameProps) => {
  if (!onClose) {
    return (
      <Page title='' pageType={PAGE_NAMES.CHARTS}>
        {header && <div className='single-chart-view-header'>{header}</div>}
        {children}
      </Page>
    );
  }

  return (
    <ChartModal onClose={onClose} header={header}>
      {children}
    </ChartModal>
  );
};

// Anything open ON TOP of the chart modal that Esc should close first: a real Bootstrap modal this
// view opened (the fan-out confirm, Save as a new chart, sharing — Bootstrap marks <body> while one is
// up), the date picker's portaled calendar, or an open dropdown menu. Not hover popovers (e.g. the
// title's info icon): they don't close on Esc, so with the mouse resting on one Esc did nothing.
const isOverlayOpenAbove = (): boolean =>
  document.body.classList.contains('modal-open') ||
  document.querySelector('.dp-date-container, .dropdown-menu.show') !== null;

interface ChartModalProps extends PropsWithChildren {
  onClose: () => void;
  header?: ReactNode;
}

// The chart-library modal itself (see ChartViewFrame). Closes on the X, Esc, or a click on the
// blurred backdrop. No `aria-modal`: the agent drawer beside it stays usable on purpose (the agent
// edits whichever chart is open), so only the library behind it is made `inert` (see ChartsPage).
const ChartModal = ({ onClose, header, children }: ChartModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Whether the current click STARTED on the backdrop, so a drag that begins inside the dialog (e.g.
  // selecting the title's text) and ends over the backdrop doesn't close it.
  const pressedOnBackdrop = useRef(false);

  // Move focus into the dialog on open, and back to whatever had it (the library card) on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || isOverlayOpenAbove()) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a mouse convenience; Esc and the close button are the keyboard paths.
    <div
      className='single-chart-view-modal-backdrop'
      data-testid='chart-modal-backdrop'
      onMouseDown={e => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={e => {
        if (pressedOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className='single-chart-view-modal'
        data-testid='chart-modal'
        role='dialog'
        aria-label='Chart'
        tabIndex={-1}
      >
        <div className='single-chart-view-header'>
          {header}
          <IconButton
            design={Design.NEUTRAL}
            appearance={Appearance.EMPTY}
            tooltip={{ text: 'Back to chart library' }}
            aria-label='Close chart'
            data-testid='chart-modal-close'
            onClick={onClose}
          >
            <MdClose />
          </IconButton>
        </div>
        {children}
      </div>
    </div>
  );
};

interface PendingSharingNoteProps {
  // The RESOLVED levels this draft will save with — after `applyCeiling`, not the raw request (see
  // `effectiveDraftLevels`'s note in SingleChartView). Only whichever half the request actually named
  // is passed; the other stays `undefined` so the note makes no promise about a field nobody asked
  // about.
  visibleTo?: number;
  editableBy?: number;
}

/**
 * A draft's requested audience, in words, beside its Save chart button.
 *
 * NOT decoration. A draft has no row, so a chat-requested audience cannot be shown by the sharing
 * control — there is nothing for that control to read. Without this line a user who asked for an
 * audience has no way to know the request was accepted until after they save, and "it did the wrong
 * thing and said nothing" is the failure this whole feature exists to remove.
 *
 * `roleLevelLabel` on the resolved LEVEL, not the audience name: the name is a wire value between the
 * agent and this code, and nothing a person should read — and the level may have been narrowed by
 * `applyCeiling` since the request named it, so it can differ from the audience the request asked for.
 *
 * Exported only so the dashboarding-permissions story shows THIS component rather than a copy of
 * its markup — a copy would drift, and the point of that story is what this actually looks like.
 * Nothing else imports it.
 */
export const PendingSharingNote = ({ visibleTo, editableBy }: PendingSharingNoteProps) => (
  <span className='single-chart-view-pending-sharing'>
    {visibleTo !== undefined && `Will be shared with: ${roleLevelLabel(visibleTo)}`}
    {visibleTo !== undefined && editableBy !== undefined && ' · '}
    {editableBy !== undefined && `Editable by: ${roleLevelLabel(editableBy)}`}
    {' · applied when you save'}
  </span>
);

interface ChartDownloadButtonProps {
  config: ChartConfig;
  data: QueryResponse;
}

// A direct download button rather than ChartActionsMenu's dropdown: that menu's second entry (Remove
// from dashboard) only ever appears on a dashboard card, so on this page it's a ⋮ menu with exactly
// one option in it. Reuses the same export utils/hook the menu's Download XLSX entry does. Its own
// component (not inline in SingleChartView) purely so useFieldMetaCtx's hooks have a render lifecycle
// that only starts once `data` exists — SingleChartView itself can't call it unconditionally ahead of
// its own loading/error early returns.
const ChartDownloadButton = ({ config, data }: ChartDownloadButtonProps) => {
  const { cid: company } = useCompanyInfo();
  const ctx = useFieldMetaCtx(config);

  // Built on demand rather than on render: for a wide table that is real work, and a button nobody
  // clicks should cost nothing.
  const handleDownload = () =>
    exportMultiSheetToExcel(
      chartExportSheets(config, data, ctx),
      chartExportFileName(company, config.title)
    );

  return (
    <IconButton
      design={Design.NEUTRAL}
      size={Size.MD}
      disabled={!chartHasData(data)}
      tooltip={{ text: 'Download XLSX' }}
      onClick={handleDownload}
    >
      <MdOutlineFileDownload />
    </IconButton>
  );
};
